import { useEffect, useRef, useState } from "react";
import { Button, Input, Table } from "reactstrap";
import AdminInput from "./AdminInput";
import Label from "./Label";
import EditEtap from "./EditEtap";
import EditLinkImprezy from "./EditLinkImprezy";
import LinkType from "../enums/LinkType";

const ImprezaDetails = ({ details, admin, onDraftChange }) => {
  const normalizeLinks = (sourceLinks = []) => {
    return (sourceLinks ?? []).map((link) => ({
      ...link,
      OriginalImpreza_Id: link.OriginalImpreza_Id ?? link.Impreza_Id ?? "",
      OriginalLinkType: link.OriginalLinkType ?? link.LinkType ?? "",
      OriginalLineNumber: link.OriginalLineNumber ?? link.LineNumber ?? "",
      __isNew: link.__isNew ?? false
    }));
  };

  const sameOriginalLink = (a, b) => (
    String(a.OriginalImpreza_Id ?? a.Impreza_Id ?? "") === String(b.OriginalImpreza_Id ?? b.Impreza_Id ?? "") &&
    String(a.OriginalLinkType ?? a.LinkType ?? "") === String(b.OriginalLinkType ?? b.LinkType ?? "") &&
    String(a.OriginalLineNumber ?? a.LineNumber ?? "") === String(b.OriginalLineNumber ?? b.LineNumber ?? "")
  );

  const [name, setName] = useState(details?.Nazwa);
  const [city, setCity] = useState(details?.Miejsce);
  const [district, setDistrict] = useState(details?.Wojewodztwo);
  const [from, setFrom] = useState(details?.TerminOd);
  const [to, setTo] = useState(details?.TerminDo);
  const [logoLink, setLogoLink] = useState(details?.Logo);
  const [links, setLinks] = useState(normalizeLinks(details?.Links ?? []));
  const [etapy, setEtapy] = useState(details?.Etapy ?? []);
  const [organizatorzy, setOrganizatorzy] = useState(details?.Organizatorzy ?? []);
  const [newId, setNewId] = useState(-1);
  const [editEtapOpen, setEditEtapOpen] = useState(false);
  const [etapToEdit, setEtapToEdit] = useState(null);
  const [editLinkOpen, setEditLinkOpen] = useState(false);
  const [linkToEdit, setLinkToEdit] = useState(null);
  const [dragIndex, setDragIndex] = useState(null);
  const loadedDetailsId = useRef(null);

  useEffect(() => {
    if (!details?.Id || loadedDetailsId.current === details.Id) {
      return;
    }

    loadedDetailsId.current = details.Id;
    setName(details?.Nazwa);
    setCity(details?.Miejsce);
    setDistrict(details?.Wojewodztwo);
    setFrom(details?.TerminOd);
    setTo(details?.TerminDo);
    setLogoLink(details?.Logo);
    setLinks(normalizeLinks(details?.Links ?? []));
    setEtapy(details?.Etapy ?? []);
    setOrganizatorzy(details?.Organizatorzy ?? []);
  }, [details]);

  useEffect(() => {
    setLinks((prevLinks) => {
      const nextLinks = [...prevLinks];
      const logoIndex = nextLinks.findIndex((link) => Number(link.LinkType) === 1);

      if (!logoLink) {
        if (logoIndex === -1) {
          return prevLinks;
        }

        nextLinks.splice(logoIndex, 1);
        return nextLinks;
      }

      const logoItem = {
        ...(logoIndex !== -1 ? nextLinks[logoIndex] : {}),
        Impreza_Id: details?.Id,
        Nazwa: "Logo",
        Url: logoLink,
        LineNumber: logoIndex !== -1 ? (nextLinks[logoIndex].LineNumber ?? 1) : 1,
        LinkType: 1,
        __isNew: false,
        OriginalImpreza_Id: details?.Id,
        OriginalLinkType: 1,
        OriginalLineNumber: logoIndex !== -1 ? (nextLinks[logoIndex].LineNumber ?? 1) : 1
      };

      if (logoIndex !== -1) {
        nextLinks[logoIndex] = logoItem;
      } else {
        nextLinks.unshift(logoItem);
      }

      return nextLinks;
    });
  }, [logoLink, details?.Id]);

  useEffect(() => {
    onDraftChange?.({
      Id: details?.Id,
      Nazwa: name,
      Miejsce: city,
      Wojewodztwo: district,
      TerminOd: from,
      TerminDo: to,
      Logo: logoLink,
      Links: links
    });
  }, [details?.Id, name, city, district, from, to, logoLink, links, onDraftChange]);

  const handleAddEtap = () => {
    setEtapToEdit({
      Id: newId
    });
    setNewId((prev) => prev - 1);
    setEditEtapOpen(true);
  };

  const handleSaveEtap = (etap) => {
    setEtapy([...etapy, etap]);
  };

  const handleCancelEditEtap = () => {
    setEditEtapOpen(false);
    setEtapToEdit(null);
  };

  const handleAddLink = () => {
    const nextLineNumber = links.length > 0
      ? Math.max(...links.map((link) => Number(link.LineNumber) || 0)) + 1
      : 1;

    setLinkToEdit({
      Impreza_Id: details.Id,
      Nazwa: "",
      Url: "",
      LineNumber: nextLineNumber,
      LinkType: 2,
      __isNew: true
    });
    setEditLinkOpen(true);
  };

  const handleEditLink = (link) => {
    setLinkToEdit(link);
    setEditLinkOpen(true);
  };

  const handleCancelEditLink = () => {
    setEditLinkOpen(false);
    setLinkToEdit(null);
  };

  const handleSaveLink = (link) => {
    const normalizedLink = normalizeLinks([link])[0];

    setLinks((prevLinks) => {
      const withoutOriginal = prevLinks.filter((item) => !sameOriginalLink(item, normalizedLink));
      const insertIndex = Math.max(
        0,
        Math.min(withoutOriginal.length, (Number(normalizedLink.LineNumber) || withoutOriginal.length + 1) - 1)
      );

      withoutOriginal.splice(insertIndex, 0, {
        ...normalizedLink,
        __isNew: false,
        OriginalImpreza_Id: normalizedLink.Impreza_Id,
        OriginalLinkType: normalizedLink.LinkType,
        OriginalLineNumber: normalizedLink.LineNumber
      });

      return withoutOriginal.map((item, index) => ({
        ...item,
        LineNumber: index + 1
      }));
    });

    if (Number(normalizedLink.LinkType) === 1) {
      setLogoLink(normalizedLink.Url);
    }

    setEditLinkOpen(false);
    setLinkToEdit(null);
  };

  const persistLinkOrder = (orderedLinks) => {
    if (!orderedLinks || orderedLinks.length === 0) {
      return;
    }

    const normalized = orderedLinks.map((link, index) => ({
      ...link,
      LineNumber: index + 1
    }));

    setLinks(normalized);
    setDragIndex(null);
  };

  const handleDragStart = (index, e) => {
    setDragIndex(index);
    if (e?.dataTransfer) {
      e.dataTransfer.effectAllowed = "move";
      e.dataTransfer.setData("text/plain", String(index));
    }
  };

  const handleDragOver = (e) => {
    if (admin) {
      e.preventDefault();
    }
  };

  const handleDrop = (index, e) => {
    e.preventDefault();
    if (!admin || dragIndex === null || dragIndex === index) {
      return;
    }

    const next = [...links];
    const [dragged] = next.splice(dragIndex, 1);
    next.splice(index, 0, dragged);
    persistLinkOrder(next);
  };

  const handleDragEnd = () => {
    setDragIndex(null);
  };

  const handleDeleteLink = (link) => {
    if (!window.confirm(`Usunąć link "${link.Nazwa}"?`)) {
      return;
    }

    if (Number(link.LinkType) === 1) {
      setLogoLink("");
    }

    setLinks((prevLinks) => prevLinks
      .filter((item) => !sameOriginalLink(item, link))
      .map((item, index) => ({
        ...item,
        LineNumber: index + 1
      }))
    );
  };

  const renderEtapy = () => {
    return etapy && (
      <Table striped>
        <thead>
          <tr>
            <th scope="col">Kategoria</th>
            <th scope="col">Numer</th>
            <th scope="col">Nazwa</th>
            <th scope="col">Data</th>
            <th scope="col">Dzień/Noc</th>
            <th scope="col">Budowniczy</th>
            <th scope="col">Ilość PK</th>
            <th scope="col">Długość</th>
            <th scope="col">Limit podstawowy</th>
            <th scope="col">Limit spóźnień</th>
          </tr>
        </thead>
        <tbody>
          {etapy &&
            etapy.length > 0 &&
            etapy.map((etap) => (
              <tr key={etap.Id}>
                <td>{etap.Kategoria}</td>
                <td>{etap.Numer}</td>
                <td>{etap.Nazwa}</td>
                <td>{etap.Data}</td>
                <td><i className={`fa fa-${etap.DzienNoc === 'N' ? 'moon' : 'sun'}-o`}></i></td>
                <td>{(etap.Budowniczy ?? []).map(x => <span key={x.Budowniczy_Id ?? x}>{x.BudowniczyName ?? x}</span>)}</td>
                <td>{etap.PunktyKontrolne}</td>
                <td>{etap.Dlugosc}</td>
                <td>{etap.LimitPodstawowy}</td>
                <td>{etap.LimitSpoznien}</td>
              </tr>
            ))}
        </tbody>
      </Table>);
  };

  const renderLinks = () => {
    return (
      <div className="table-responsive">
        <Table striped hover>
          <thead>
            <tr>
              {admin && <th scope="col">Przeciągnij</th>}
              <th scope="col">Line</th>
              <th scope="col">Typ</th>
              <th scope="col">Nazwa</th>
              <th scope="col">Url</th>
              {admin && <th scope="col">Akcje</th>}
            </tr>
          </thead>
          <tbody>
            {links && links.length > 0 ? links.map((link, index) => {
              const linkType = LinkType[link.LinkType];
              return (
                <tr
                  key={`${link.Impreza_Id}-${link.LinkType}-${link.LineNumber}-${link.Nazwa}`}
                  draggable={admin}
                  onDragStart={(e) => handleDragStart(index, e)}
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(index, e)}
                  onDragEnd={handleDragEnd}
                  style={dragIndex === index ? { opacity: 0.5 } : undefined}
                >
                  {admin && (
                    <td className="text-center" style={{ cursor: 'grab' }}>
                      <i className="fa fa-bars"></i>
                    </td>
                  )}
                  <td>{link.LineNumber ?? linkType?.lineNumber ?? '-'}</td>
                  <td>{linkType?.description ?? link.LinkType}</td>
                  <td>{link.Nazwa}</td>
                  <td>
                    <a href={link.Url} target="_blank" rel="noopener noreferrer">
                      {link.Url}
                    </a>
                  </td>
                  {admin && (
                    <td className="text-nowrap">
                      <Button color="link" className="p-0 mr-2" onClick={() => handleEditLink(link)}>
                        <i className="fa fa-pencil"></i>
                      </Button>
                      <Button color="link" className="p-0 text-danger" onClick={() => handleDeleteLink(link)}>
                        <i className="fa fa-trash"></i>
                      </Button>
                    </td>
                  )}
                </tr>
              );
            }) : (
              <tr>
                <td colSpan={admin ? 6 : 4}>Brak linków dla tej imprezy</td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>
    );
  };

  return (
    <>
      <div className="impreza-details-container">
        <div className="impreza-details-group">
          <img src={logoLink} alt={details?.Nazwa} />
          {admin && (
            <Input
              type="text"
              autoComplete="off"
              value={logoLink}
              onChange={(e) => setLogoLink(e.target.value)}
            />
          )}
        </div>
        <div className="impreza-details-group">
            <Label>Nazwa Imprezy</Label>
            <AdminInput
              value={name}
              onChange={(e) => setName(e.target.value)}
              isAdmin={admin}
            />
            <Label>Termin</Label>
            {admin
              ? (<div className="inline-container">
                  <Label>Od</Label>
                  <AdminInput
                    type="date"
                    value={from}
                    onChange={(e) => setFrom(e.target.value)}
                    isAdmin={true}
                  />
                  <Label>Do</Label>
                  <AdminInput
                    type="date"
                    value={to}
                    onChange={(e) => setTo(e.target.value)}
                    isAdmin={true}
                  />
                </div>)
              : <AdminInput
                  value={`${from}${to ? (` - ${to}`) : ''}`}
                  isAdmin={false}
                />}
            <Label>Miejsce</Label>
            <AdminInput
              value={city}
              onChange={(e) => setCity(e.target.value)}
              isAdmin={admin}
            />
            <Label>Województwo</Label>
            <AdminInput
              value={district}
              onChange={(e) => setDistrict(e.target.value)}
              isAdmin={admin}
            />
            <Label>Organizatorzy</Label>
            {organizatorzy &&
              organizatorzy.length > 0 &&
              organizatorzy.map((organizator) => {
                return organizator.Web
                  ? <a key={organizator.Id} href={organizator.Web} target="_blank" rel="noopener noreferrer">{organizator.Nazwa}</a>
                  : <span key={organizator.Id}>{organizator.Nazwa}</span>
              })}
        </div>
        <div className="impreza-details-group">
          <Label>Kadra</Label>
        </div>
        <div className="impreza-details-group">
          <div className="d-flex align-items-center justify-content-between">
            <Label>Linki</Label>
            {admin && (
              <i className="fa fa-plus-circle react-add-icon" onClick={handleAddLink}></i>
            )}
          </div>
          {renderLinks()}
        </div>
        <div className="container-fluid d-flex flex-wrap">
          <div className="d-flex"><Label>Etapy</Label>
            {admin && (
              <i className="fa fa-plus-circle react-add-icon" onClick={handleAddEtap}></i>
            )}
          </div>
          <div className="table-responsive">
            {renderEtapy()}
          </div>
        </div>
      </div>

      {editEtapOpen && etapToEdit && (
        <EditEtap
          cancel={handleCancelEditEtap}
          handleSave={handleSaveEtap}
          etap={etapToEdit}
          terminOd={from}
        />
      )}

      {editLinkOpen && linkToEdit && (
        <EditLinkImprezy
          cancel={handleCancelEditLink}
          handleSave={handleSaveLink}
          link={linkToEdit}
        />
      )}
    </>
  );
};

export default ImprezaDetails;
